import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { Container, Card } from "react-bootstrap";
import "../../styles/ReferenceCheckResultPage.css";

const ReferenceCheckResultPage = () => {
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const navigate = useNavigate();
  const { checkId } = useParams();
  const token = localStorage.getItem("token");

  const authHeader = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
    }),
    [token]
  );

  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const loadResult = async () => {
      try {
        const res = await axios.get(
          `${BASE_URL}/api/reference-check/result/${checkId}`,
          {
            headers: authHeader,
          }
        );
        setData(res.data);
      } catch (err) {
        console.error("Failed to load reference result:", err);
        setError(err.response?.data?.message || "Failed to load result.");
      }
    };

    loadResult();
  }, [BASE_URL, authHeader, checkId]);

  const check = data?.check;
  const answers = data?.answers || [];

  const formatDate = (value) => {
    if (!value) return "-";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return String(value).slice(0, 10);
    return parsed.toLocaleDateString("en-CA", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatAnswer = (value) => {
    if (!value) return "-";
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return parsed.join(", ");
      }
    } catch {
      return value;
    }
    return value;
  };

  const statusKey = (check?.rc_status || "unknown").toLowerCase();
  const statusLabel = check?.rc_status
    ? `${check.rc_status.charAt(0).toUpperCase()}${check.rc_status.slice(1)}`
    : "Unknown";

  return (
    <Container className="reference-check-result-page py-4">
      <div className="reference-check-hero mb-4">
        <div className="reference-check-hero-copy">
          <div className="reference-check-result-kicker">Reference Check</div>
          <h1 className="reference-check-result-title">Reference Check Result</h1>
          <p className="reference-check-result-intro mb-0">
            Review the employer feedback, response status, and question-by-question
            results in one place.
          </p>
        </div>
        <button
          type="button"
          className="reference-check-return-btn"
          onClick={() => navigate(-1)}
        >
          Return
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {check && (
        <Card className="reference-check-summary-card mb-4 border-0">
          <Card.Body className="p-0">
            <div className="reference-check-summary-top">
              <div>
                <div className="reference-check-summary-eyebrow">
                  Candidate Snapshot
                </div>
                <div className="reference-check-summary-name">
                  {check.first_name} {check.last_name}
                </div>
                <div className="reference-check-summary-role">
                  {check.wh_position || "-"}
                </div>
              </div>
              <div
                className={`reference-check-status-pill reference-check-status-${statusKey}`}
              >
                <span className="reference-check-status-dot" />
                {statusLabel}
              </div>
            </div>

            <div className="reference-check-summary-grid">
              <div className="reference-check-stat-block">
                <div className="reference-check-stat-label">Applicant</div>
                <div className="reference-check-stat-value">
                  {check.first_name} {check.last_name}
                </div>
              </div>
              <div className="reference-check-stat-block">
                <div className="reference-check-stat-label">Company</div>
                <div className="reference-check-stat-value">
                  {check.wh_name || "-"}
                </div>
              </div>
              <div className="reference-check-stat-block">
                <div className="reference-check-stat-label">Position</div>
                <div className="reference-check-stat-value">
                  {check.wh_position || "-"}
                </div>
              </div>
              <div className="reference-check-stat-block">
                <div className="reference-check-stat-label">Sent</div>
                <div className="reference-check-stat-value">
                  {formatDate(check.rc_sent_at)}
                </div>
              </div>
              <div className="reference-check-stat-block">
                <div className="reference-check-stat-label">Completed</div>
                <div className="reference-check-stat-value">
                  {formatDate(check.rc_completed_at)}
                </div>
              </div>
              <div className="reference-check-stat-block">
                <div className="reference-check-stat-label">Status</div>
                <div className="reference-check-stat-value">{statusLabel}</div>
              </div>
            </div>
          </Card.Body>
        </Card>
      )}

      {answers.length > 0 && (
        <div className="reference-check-answers-header">
          <div>
            <div className="reference-check-section-label">Responses</div>
            <h2 className="reference-check-section-title mb-0">
              Employer Feedback
            </h2>
          </div>
          <div className="reference-check-section-count">
            {answers.length} question{answers.length === 1 ? "" : "s"}
          </div>
        </div>
      )}

      <div className="reference-check-answers-list">
        {answers.map((answer, index) => (
          <Card key={answer.rca_id} className="reference-check-answer-card border-0">
            <Card.Body>
              <div className="reference-check-answer-header">
                <div className="reference-check-answer-index">
                  {String(index + 1).padStart(2, "0")}
                </div>
                <div className="reference-check-answer-heading">
                  <div className="reference-check-answer-title">
                    {answer.rcq_question}
                  </div>
                  {answer.rcq_description && (
                    <div className="reference-check-answer-subtitle">
                      {answer.rcq_description}
                    </div>
                  )}
                </div>
              </div>
              <div className="reference-check-answer-body">
                {formatAnswer(answer.rca_answer)}
              </div>
            </Card.Body>
          </Card>
        ))}

        {answers.length === 0 && !error && (
          <Card className="reference-check-empty-card border-0">
            <Card.Body>
              <div className="reference-check-empty-title">
                No reference responses available
              </div>
              <div className="reference-check-empty-body">
                This result does not have any recorded answers yet.
              </div>
            </Card.Body>
          </Card>
        )}
      </div>
    </Container>
  );
};

export default ReferenceCheckResultPage;
